import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Wallet, AlertTriangle, CheckCircle2, TrendingDown, Sparkles, ArrowRight } from 'lucide-react';
import type { InsumoVista, StockCriticoVista, SugerenciaCargaDelDiaVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';
import { NumberField } from '@renderer/components/ui/NumberField';

/**
 * Sprint 39 — Pantalla de apertura del día.
 *
 * Se muestra cuando NO hay corte de caja abierto. Bloquea el acceso al
 * dashboard normal hasta que la dueña abra la caja explícitamente.
 *
 * Layout:
 *   1. Saludo + fecha grande.
 *   2. Resumen de inventario con semáforo (qué tiene, qué falta).
 *   3. Sugerencia de precarga (si aplica).
 *   4. Apertura de caja — sólo dueña. Mesero ve mensaje informativo.
 */
interface Props {
  /** Callback cuando se abre la caja exitosamente. */
  onCajaAbierta: () => void;
}

interface ResumenInsumos {
  total: number;
  agotados: number;
  criticos: number;
  bajos: number;
  ok: number;
  insumos: InsumoVista[];
  criticosDetalle: StockCriticoVista[];
}

function fechaLarga(): string {
  return new Date().toLocaleDateString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function semaforoDe(insumo: InsumoVista): 'rojo' | 'amarillo' | 'verde' | 'gris' {
  const stock = insumo.stockActual ?? 0;
  const minimo = insumo.stockMinimo ?? 0;
  if (stock <= 0) return 'gris';
  if (minimo > 0 && stock <= minimo) return 'rojo';
  if (minimo > 0 && stock <= minimo * 2) return 'amarillo';
  return 'verde';
}

export function AperturaDelDia({ onCajaAbierta }: Props) {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';

  const [resumen, setResumen] = useState<ResumenInsumos | null>(null);
  const [sugerencia, setSugerencia] = useState<SugerenciaCargaDelDiaVista | null>(null);
  const [cargando, setCargando] = useState(true);
  // Apertura de caja: monto inicial en centavos.
  const [montoInicialCent, setMontoInicialCent] = useState(0);
  const [abriendoCaja, setAbriendoCaja] = useState(false);
  const [errorCaja, setErrorCaja] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const cargar = async () => {
      setCargando(true);
      const [ins, crit, sug] = await Promise.all([
        window.api.insumos.listar(),
        window.api.insumos.stockCritico(),
        window.api.insumos.sugerenciaCargaDelDia().catch(() => ({ ok: false } as const)),
      ]);
      if (!alive) return;
      const insumos = ins.ok ? ins.data : [];
      const criticosDetalle = crit.ok ? crit.data : [];
      let agotados = 0,
        criticos = 0,
        bajos = 0,
        ok = 0;
      for (const i of insumos) {
        const s = semaforoDe(i);
        if (s === 'gris') agotados++;
        else if (s === 'rojo') criticos++;
        else if (s === 'amarillo') bajos++;
        else ok++;
      }
      setResumen({
        total: insumos.length,
        agotados,
        criticos,
        bajos,
        ok,
        insumos: insumos.slice().sort((a, b) => a.nombre.localeCompare(b.nombre)),
        criticosDetalle,
      });
      if (sug.ok) setSugerencia(sug.data);
      setCargando(false);
    };
    cargar();
    return () => {
      alive = false;
    };
  }, []);

  const abrirCaja = async () => {
    if (!sesion) return;
    setAbriendoCaja(true);
    setErrorCaja(null);
    const res = await window.api.corte.abrir(montoInicialCent, sesion.usuarioId);
    setAbriendoCaja(false);
    if (res.ok) {
      onCajaAbierta();
    } else {
      setErrorCaja(traducirErrorBackend(res.error, res.codigo));
    }
  };

  if (!sesion) return null;
  const esDueno = sesion.rol === 'dueño';

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* Encabezado de bienvenida */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-200 p-3 shrink-0">
            <Sun className="h-7 w-7 text-amber-800" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-amber-900">
              ¡Buen día, {sesion.nombre}!
            </h1>
            <p className="text-amber-800 mt-1 capitalize">{fechaLarga()}</p>
            <p className="text-sm text-amber-700 mt-2">
              {esDueno
                ? 'Antes de empezar a vender, vamos a revisar el inventario y abrir la caja.'
                : 'Espera a que el dueño abra la caja para poder empezar a tomar pedidos.'}
            </p>
          </div>
        </div>
      </div>

      {cargando ? (
        <div className="card text-center py-8 text-slate-400">Cargando estado del día…</div>
      ) : !resumen ? null : (
        <>
          {/* Resumen de inventario con semáforo */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-bold">Existencias para hoy</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Chip color="green" label="En orden" valor={resumen.ok} icon={<CheckCircle2 className="h-4 w-4" />} />
              <Chip color="amber" label="Bajos" valor={resumen.bajos} icon={<TrendingDown className="h-4 w-4" />} />
              <Chip color="red" label="Críticos" valor={resumen.criticos} icon={<AlertTriangle className="h-4 w-4" />} />
              <Chip color="slate" label="Agotados" valor={resumen.agotados} icon={<AlertTriangle className="h-4 w-4" />} />
            </div>

            {/* Tabla de insumos */}
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="px-2 py-1.5">Ingrediente</th>
                    <th className="px-2 py-1.5 text-right">Tengo</th>
                    <th className="px-2 py-1.5 text-right">Mínimo</th>
                    <th className="px-2 py-1.5 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.insumos.map((i) => {
                    const s = semaforoDe(i);
                    const colorTxt =
                      s === 'gris' ? 'text-slate-500'
                      : s === 'rojo' ? 'text-red-700'
                      : s === 'amarillo' ? 'text-amber-700'
                      : 'text-emerald-700';
                    const label =
                      s === 'gris' ? '⚫ Agotado'
                      : s === 'rojo' ? '🔴 Crítico'
                      : s === 'amarillo' ? '🟡 Bajo'
                      : '🟢 OK';
                    return (
                      <tr key={i.id} className="border-b border-slate-100">
                        <td className="px-2 py-1.5">{i.nombre}</td>
                        <td className={`px-2 py-1.5 text-right font-mono ${colorTxt}`}>
                          {(i.stockActual ?? 0).toFixed(2)} {i.unidad}
                        </td>
                        <td className="px-2 py-1.5 text-right text-slate-500 font-mono text-xs">
                          {(i.stockMinimo ?? 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-1.5 text-center text-xs font-semibold ${colorTxt}`}>
                          {label}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sugerencia de precarga */}
          {(resumen.criticos > 0 || resumen.agotados > 0) && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-amber-200 p-2 shrink-0">
                  <Sparkles className="h-5 w-5 text-amber-800" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-amber-900">
                    Tienes insumos bajos o agotados
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    {sugerencia && sugerencia.insumos.some((s) => s.sugerencia > 0)
                      ? `Te sugerimos cargar inventario antes de abrir el día. Patrón basado en los últimos ${sugerencia.nombreDiaObjetivo ?? ''}s.`
                      : 'Te recomendamos revisar y cargar los insumos críticos antes de abrir el día.'}
                  </p>
                  <button
                    onClick={() => navigate('/precargar-inventario')}
                    className="mt-3 btn-primary"
                  >
                    Cargar inventario ahora <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Apertura de caja */}
          {esDueno ? (
            <div className="card border-2 border-emerald-300 bg-emerald-50/30">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="h-6 w-6 text-emerald-700" />
                <h2 className="text-lg font-bold text-emerald-900">Abrir caja para iniciar el día</h2>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Sin caja abierta, el sistema no permite tomar pedidos. Captura cuánto efectivo tienes
                como fondo inicial y abre el día.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                <div className="flex-1">
                  <label className="label" htmlFor="apertura-monto">
                    Efectivo inicial en caja
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-slate-700">{monedaSimbolo}</span>
                    <NumberField
                      id="apertura-monto"
                      className="input text-2xl font-mono text-right h-14"
                      min={0}
                      decimals={2}
                      value={centavosAPesos(montoInicialCent)}
                      onChange={(n) => setMontoInicialCent(pesosACentavos(n))}
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Si no llevas fondo, deja en 0. Se usa solo para calcular tu diferencia al cerrar.
                  </p>
                </div>
                <button
                  onClick={abrirCaja}
                  disabled={abriendoCaja}
                  className="btn-primary btn-lg shrink-0 sm:self-end h-14"
                >
                  <Wallet className="h-5 w-5" />
                  {abriendoCaja ? 'Abriendo…' : 'Abrir caja'}
                </button>
              </div>
              {errorCaja && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
                  {errorCaja}
                </div>
              )}
            </div>
          ) : (
            <div className="card border-2 border-slate-300 bg-slate-50">
              <div className="flex items-center gap-3">
                <Wallet className="h-6 w-6 text-slate-500" />
                <div>
                  <h2 className="font-bold text-slate-700">Caja cerrada</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Espera a que la dueña abra la caja para poder tomar pedidos.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Chip({
  color,
  label,
  valor,
  icon,
}: {
  color: 'green' | 'amber' | 'red' | 'slate';
  label: string;
  valor: number;
  icon: React.ReactNode;
}) {
  const map = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  } as const;
  return (
    <div className={`rounded-xl border-2 p-3 ${map[color]}`}>
      <div className="flex items-center gap-1 text-xs uppercase tracking-wide opacity-80 font-semibold">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-3xl font-bold font-mono mt-1">{valor}</div>
    </div>
  );
}
